/**
 * §6.10 — the payments that leave on a schedule and have no document.
 *
 * WHAT THIS SCREEN IS FOR, WHICH IS TWO THINGS
 *
 * Recording what will happen, so the forecast can see it, and showing what was
 * expected and did not happen. The second is why the table earns its place. A
 * commitment that only fed a forecast would be a convenience; one that says
 * "you expected $500 on the 18th and nothing left the account" is a control,
 * and it is the only thing in this application that can notice a transaction
 * that never occurred. A ledger records what happened; nothing in it has a
 * shape for what didn't.
 *
 * SUGGESTIONS ARE OFFERED AND NEVER APPLIED
 *
 * The server detects regular payments; accepting one opens the form with its
 * fields filled, and the operator still presses Record. prototype/FINDINGS.md's
 * rule about the account field is the same argument: "a field that is right
 * most of the time stops being read". A suggestion silently becoming a
 * commitment would put a figure on the forecast that nobody decided on.
 *
 * NOTHING HERE IS EDITED
 *
 * Migration 0037 refuses every change but the end date, so this screen has no
 * edit control to offer. A commitment that really ran is ENDED, keeping its
 * amount and cadence so a missed-payment check over a past month still judges
 * that month against the terms in force in it. One entered wrongly is REMOVED.
 * Those are different buttons because they are different statements, and a
 * single "delete" would quietly rewrite the history in one of the two cases.
 */

import { useCan } from '../auth/AuthProvider.js';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ApiError,
  api,
  type Account,
  type CommitmentCandidate,
  type CommitmentState,
  type CommitmentTiming,
  type IgnoredPattern,
  type MissedCommitment,
  type PayFrequency,
  type RecurringCommitment,
} from '../api.js';
import { Combobox } from '../components/Combobox.js';
import { Toast } from '../components/Toast.js';
import { formatCents, parseCents } from '../lib/money.js';

const CADENCES: { value: PayFrequency; label: string }[] = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Every Two Weeks' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
];

const CADENCE_LABELS = Object.fromEntries(
  CADENCES.map((option) => [option.value, option.label]),
) as Record<PayFrequency, string>;

interface FormState {
  label: string;
  counterparty: string;
  accountId: string;
  amount: string;
  cadence: PayFrequency;
  timing: CommitmentTiming;
  startsOn: string;
}

/** Any day in the month is for a cadence with one date a month or fewer (0093). */
const takesInMonth = (cadence: PayFrequency) => cadence === 'monthly' || cadence === 'quarterly';

/**
 * The tabs, and why Ignored sits beside the other two.
 *
 * Active and Ended are two states of a COMMITMENT; Ignored is a refused
 * SUGGESTION, which is a different kind of row entirely -- no amount, no
 * cadence, nothing affirmed. They share a tab strip because they answer one
 * question the operator actually has ("what have I already dealt with?") and
 * because three short lists are worse as three separate places to look.
 *
 * THERE IS NO "REMOVED" TAB, and one was built on 2026-09-17 before being taken
 * out the same day. Remove is the correction path for a row 0037 makes
 * un-editable: it means the commitment should never have been recorded, so a
 * list of removed ones is a list of typos. Migration 0038's header carries the
 * rest of the argument.
 *
 * The Ignored tab renders its own table, not the commitments one, and that is
 * the honest way to show two different shapes in one strip.
 */
const TABS = [
  { key: 'active', label: 'Active' },
  { key: 'ignored', label: 'Ignored' },
  { key: 'ended', label: 'Ended' },
] as const;

type Tab = (typeof TABS)[number]['key'];

const EMPTY: FormState = {
  label: '',
  counterparty: '',
  accountId: '',
  amount: '',
  cadence: 'monthly',
  timing: 'on_day',
  startsOn: '',
};

export function CommitmentsPage() {
  // What this screen offers (0069); the API refuses the rest either way.
  const can = useCan();
  const mayEdit = can('edit_entries');
  const [commitments, setCommitments] = useState<RecurringCommitment[] | null>(null);
  const [missed, setMissed] = useState<MissedCommitment[]>([]);
  const [candidates, setCandidates] = useState<CommitmentCandidate[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [ignored, setIgnored] = useState<IgnoredPattern[]>([]);
  const [tab, setTab] = useState<Tab>('active');
  const [form, setForm] = useState<FormState>(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  /** Which commitment is being ended, and the date typed for it. */
  const [ending, setEnding] = useState<{ id: string; endsOn: string } | null>(null);
  /**
   * Which one is being removed, awaiting confirmation.
   *
   * Remove is a DELETE and cannot be undone, and it sits beside End, which can.
   * The operator said on 2026-09-17 that they could not tell what Remove did --
   * a destructive button with no explanation next to a similar-looking one is
   * the thing to fix, and the confirmation is where the explanation goes.
   */
  const [removing, setRemoving] = useState<string | null>(null);

  const load = useCallback(() => {
    /*
     * The table is re-read per tab rather than fetched whole and filtered here.
     * Unlike the Home horizon, these are four DIFFERENT questions of the
     * database -- three states of a commitment and a list of refused patterns --
     * and collapsing them into one read would mean sending every removed row to
     * a screen showing the live ones.
     */
    Promise.all([
      api.listCommitments(tab === 'ignored' ? 'active' : tab),
      api.listMissedCommitments(),
      api.listCommitmentSuggestions(),
      api.listAccounts(),
      api.listIgnoredPatterns(),
    ])
      .then(([list, missedResult, suggestions, accountList, ignoredResult]) => {
        setCommitments(list.commitments);
        setMissed(missedResult.missed);
        setCandidates(suggestions.candidates);
        setAccounts(accountList.accounts);
        setIgnored(ignoredResult.ignored);
        setError(null);
      })
      .catch((caught: unknown) =>
        setError(caught instanceof ApiError ? caught.message : 'Could not load the commitments.'),
      );
  }, [tab]);

  useEffect(load, [load]);

  /**
   * Accepting a suggestion FILLS THE FORM. It does not record anything.
   *
   * The start date is the day the pattern lands on, in the month it was last
   * seen: a commitment starting on its own last occurrence means the schedule
   * carries forward from something that actually happened, rather than from a
   * date invented to be near today.
   */
  function accept(candidate: CommitmentCandidate) {
    setForm({
      label: candidate.counterparty,
      counterparty: candidate.counterparty,
      accountId: candidate.accountId,
      amount: (candidate.amountCents / 100).toFixed(2),
      cadence: 'monthly',
      timing: candidate.timing,
      startsOn: candidate.lastSeen,
    });
    setShowForm(true);
    setFormError(null);
  }

  async function record(event: React.FormEvent) {
    event.preventDefault();
    const amountCents = parseCents(form.amount);
    if (amountCents === null || amountCents <= 0) {
      setFormError('Enter an amount above zero, such as 500.00.');
      return;
    }
    if (form.accountId === '') {
      setFormError('Choose the account this is paid from.');
      return;
    }

    setSaving(true);
    try {
      const { commitment } = await api.recordCommitment({
        label: form.label,
        counterparty: form.counterparty.trim() || null,
        accountId: form.accountId,
        amountCents,
        cadence: form.cadence,
        timing: takesInMonth(form.cadence) ? form.timing : 'on_day',
        startsOn: form.startsOn,
      });
      setForm(EMPTY);
      setShowForm(false);
      setFormError(null);
      setToast(`Recorded ${commitment.label}.`);
      load();
    } catch (caught) {
      setFormError(
        caught instanceof ApiError ? caught.message : 'Could not record that commitment.',
      );
    } finally {
      setSaving(false);
    }
  }

  async function end(id: string, endsOn: string) {
    try {
      await api.endCommitment(id, endsOn);
      setEnding(null);
      setToast('Ended. Its history is kept.');
      load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not end that commitment.');
    }
  }

  /**
   * Refuses a suggestion outright.
   *
   * A different statement from Remove, which is about a commitment that was
   * entered wrongly. This says the PATTERN is not a standing order -- the
   * payments are real and will go on being counted; they are just not something
   * the operator undertakes to keep doing.
   */
  async function ignore(candidate: CommitmentCandidate) {
    try {
      await api.ignoreCommitmentPattern(candidate.counterparty, candidate.accountId);
      setToast(`${candidate.counterparty} will not be suggested again.`);
      load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not ignore that pattern.');
    }
  }

  async function unignore(pattern: IgnoredPattern) {
    try {
      await api.unignoreCommitmentPattern(pattern.id);
      setToast(`${pattern.counterparty} can be suggested again.`);
      load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not restore that pattern.');
    }
  }

  async function remove(commitment: RecurringCommitment) {
    try {
      await api.removeCommitment(commitment.id);
      setRemoving(null);
      setToast(`Removed ${commitment.label}.`);
      load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not remove that commitment.');
    }
  }

  return (
    <section className="commitments">
      <header className="row reports-head">
        <h1>Commitments</h1>
        <span className="muted">
          Payments that leave on a schedule and have no invoice. They appear on the{' '}
          <Link to="/reports/cashflow">cashflow forecast</Link>, and one that stops arriving is
          flagged here.
        </span>
        {mayEdit && (
          <button type="button" onClick={() => setShowForm((open) => !open)}>
            {showForm ? 'Cancel' : 'Record A Commitment'}
          </button>
        )}
      </header>

      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}

      {/*
        THE FLAGS COME FIRST. A missed payment is the only thing on this screen
        that cannot be found by looking somewhere else, so it is above the list
        rather than beside the row it belongs to.
      */}
      {missed.length > 0 && (
        <div className="notice notice-warn" role="status">
          <strong>Expected and not seen.</strong>
          <ul className="missed-list">
            {missed.map((row) => (
              <li key={`${row.commitmentId}-${row.expectedOn}`}>
                {row.label} — {formatCents(row.expectedCents)} expected {row.expectedOn}
                {row.seen === null ? (
                  row.timing === 'in_month' ? ', nothing found in that month.' : ', nothing found within five days.'
                ) : (
                  <>
                    , but <Link to={`/journals/${row.seen.headerId}`}>
                      {formatCents(row.seen.amountCents)} was paid on {row.seen.entryDate}
                    </Link>
                    .
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {showForm && (
        <form className="commitment-form" onSubmit={(event) => void record(event)}>
          {formError && (
            <p role="alert" className="error">
              {formError}
            </p>
          )}
          <div className="row">
            <label>
              Name{' '}
              <input
                required
                value={form.label}
                onChange={(event) => setForm({ ...form, label: event.target.value })}
                placeholder="Monthly Giving"
              />
            </label>
            <label>
              As The Bank Shows It{' '}
              <input
                value={form.counterparty}
                onChange={(event) => setForm({ ...form, counterparty: event.target.value })}
                placeholder="HOPE FOUNDATION"
              />
            </label>
          </div>
          <div className="row">
            <Combobox
              label="Account"
              value={form.accountId}
              onChange={(accountId) => setForm({ ...form, accountId })}
              options={accounts.map((account) => ({
                id: account.id,
                label: `${account.code} ${account.name}`,
                group: account.type,
              }))}
            />
            <label>
              Amount{' '}
              <input
                required
                inputMode="decimal"
                value={form.amount}
                onChange={(event) => setForm({ ...form, amount: event.target.value })}
                placeholder="500.00"
              />
            </label>
          </div>
          <div className="row">
            <label>
              Cadence{' '}
              <select
                value={form.cadence}
                onChange={(event) =>
                  setForm({ ...form, cadence: event.target.value as PayFrequency })
                }
              >
                {CADENCES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              First Payment{' '}
              <input
                required
                type="date"
                value={form.startsOn}
                onChange={(event) => setForm({ ...form, startsOn: event.target.value })}
              />
            </label>
            {takesInMonth(form.cadence) && (
              <label>
                Paid On{' '}
                <select
                  value={form.timing}
                  onChange={(event) => {
                    const timing = event.target.value as CommitmentTiming;
                    setForm((current) => ({ ...current, timing }));
                  }}
                >
                  <option value="on_day">That Day</option>
                  <option value="in_month">Any Day In The Month</option>
                </select>
              </label>
            )}
          </div>
          {/*
            Said on the form, not after the first refusal. The rule is
            surprising enough to be worth stating before somebody relies on
            being able to correct a typo in the amount later.
          */}
          <p className="muted">
            The day of the first payment is the schedule&apos;s day; paid on any day in the month,
            only its month counts. A commitment is never edited afterwards: it can be ended, or
            removed and recorded again.
          </p>
          <button type="submit" disabled={saving}>
            {saving ? 'Recording…' : 'Record'}
          </button>
        </form>
      )}

      {mayEdit && candidates.length > 0 && (
        <section className="suggestions">
          <h2>Suggested From Your Transactions</h2>
          <p className="muted">
            Regular payments no commitment covers. Accepting one fills the form; nothing is
            recorded until you press Record.
          </p>
          <ul className="suggestion-list">
            {candidates.map((candidate) => (
              <li key={`${candidate.counterparty}-${candidate.accountId}`}>
                <span>
                  <strong>{candidate.counterparty}</strong> — {formatCents(candidate.amountCents)}{' '}
                  {candidate.timing === 'in_month' ? (
                    'once a month on any day'
                  ) : (
                    <>
                      on about the {candidate.dayOfMonth}
                      {ordinal(candidate.dayOfMonth)}
                    </>
                  )}
                  , seen in {candidate.occurrences} months to {candidate.lastSeen}, to {candidate.accountCode}{' '}
                  {candidate.accountName}
                </span>
                <span className="row">
                  <button type="button" onClick={() => accept(candidate)}>
                    Use This
                  </button>
                  {/*
                    IGNORE IS NOT REMOVE. It says the pattern is not a standing
                    order -- the payments are real and still counted everywhere
                    -- where Remove says a recorded commitment should never have
                    been entered. Two different statements, so two different
                    words, and the Ignored tab is where this one can be undone.
                  */}
                  <button type="button" onClick={() => void ignore(candidate)}>
                    Ignore
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/*
        Tabs, not a checkbox. "Show Ended" could only ever answer one of the
        three questions, and it answered it by MIXING the ended rows into the
        live ones -- a stopped payment sharing a list with a running one is how
        a stopped payment goes on looking current.
      */}
      <nav className="row tabs" aria-label="Commitments">
        {TABS.map((entry) => (
          <button
            key={entry.key}
            type="button"
            className={`tab${tab === entry.key ? ' is-current' : ''}`}
            aria-current={tab === entry.key ? 'page' : undefined}
            onClick={() => setTab(entry.key)}
          >
            {entry.label}
            {entry.key === 'ignored' && ignored.length > 0 && (
              <span className="muted"> {ignored.length}</span>
            )}
          </button>
        ))}
      </nav>

      {/* The Ignored tab shows a different SHAPE, so it gets its own table. */}
      {tab === 'ignored' ? (
        ignored.length === 0 ? (
          <p className="muted">
            Nothing ignored. Pressing Ignore on a suggestion puts it here, where it can be put
            back. Nothing about the transactions changes either way — they are still categorized,
            still counted, and still on every report.
          </p>
        ) : (
          <div className="table-scroll">
            <table className="lines">
              <thead>
                <tr>
                  <th>Paid To</th>
                  <th>Account</th>
                  <th>Ignored</th>
                  <th>{/* actions */}</th>
                </tr>
              </thead>
              <tbody>
                {ignored.map((pattern) => (
                  <tr key={pattern.id}>
                    <td>{pattern.counterparty}</td>
                    <td>
                      {pattern.accountCode} {pattern.accountName}
                    </td>
                    <td>{pattern.ignoredAt.slice(0, 10)}</td>
                    <td>
                      {mayEdit && (
                        <button type="button" onClick={() => void unignore(pattern)}>
                          Suggest Again
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : commitments === null ? (
        !error && <p className="muted">Loading…</p>
      ) : commitments.length === 0 ? (
        <p className="muted">
          {tab === 'active'
            ? 'No commitments recorded. A standing payment — a monthly donation, a subscription, a loan instalment — is invisible to the forecast until it is recorded here, because the bank sends it and no invoice announces it.'
            : 'Nothing has ended. A commitment that stopped is recorded with the date it stopped, and keeps its amount and cadence so a missed payment in a past month is still judged against the terms that applied then.'}
        </p>
      ) : (
        <div className="table-scroll">
          <table className="lines">
            <thead>
              <tr>
                <th>Name</th>
                <th>Paid To</th>
                <th>Account</th>
                <th>Cadence</th>
                <th>From</th>
                <th className="num">Amount</th>
                <th>{/* actions */}</th>
              </tr>
            </thead>
            <tbody>
              {commitments.map((commitment) => (
                <tr key={commitment.id} className={commitment.endsOn === null ? '' : 'is-ended'}>
                  <td>{commitment.label}</td>
                  <td>{commitment.partyName ?? commitment.counterparty ?? '—'}</td>
                  <td>
                    {commitment.accountCode} {commitment.accountName}
                  </td>
                  <td>
                    {CADENCE_LABELS[commitment.cadence]}
                    {commitment.timing === 'in_month' && <span className="muted"> · Any Day In The Month</span>}
                  </td>
                  <td>
                    {commitment.startsOn}
                    {commitment.endsOn !== null && (
                      <span className="muted"> to {commitment.endsOn}</span>
                    )}
                  </td>
                  <td className="num">{formatCents(commitment.amountCents)}</td>
                  <td>
                    {commitment.endsOn !== null ? (
                      <span className="muted">Ended</span>
                    ) : removing === commitment.id ? (
                      /*
                        The confirmation says which verb the operator probably
                        wants, rather than asking "are you sure" -- the mistake
                        worth catching is not a misclick, it is reaching for
                        Remove when the payment really stopped.
                      */
                      <span className="row">
                        <span className="muted">
                          Delete {commitment.label}? Use End instead if it really stopped.
                        </span>
                        <button type="button" onClick={() => void remove(commitment)}>
                          Delete It
                        </button>
                        <button type="button" onClick={() => setRemoving(null)}>
                          Cancel
                        </button>
                      </span>
                    ) : ending?.id === commitment.id ? (
                      /*
                        Inline, as the Matching screen's reason form is. A modal
                        for one date field would be a new pattern for the
                        smallest confirmation on the screen.
                      */
                      <span className="row">
                        <input
                          type="date"
                          aria-label={`End date for ${commitment.label}`}
                          value={ending.endsOn}
                          onChange={(event) =>
                            setEnding({ id: commitment.id, endsOn: event.target.value })
                          }
                        />
                        <button
                          type="button"
                          disabled={ending.endsOn === ''}
                          onClick={() => void end(commitment.id, ending.endsOn)}
                        >
                          Confirm
                        </button>
                        <button type="button" onClick={() => setEnding(null)}>
                          Cancel
                        </button>
                      </span>
                    ) : !mayEdit ? null : (
                      <span className="row">
                        <button
                          type="button"
                          onClick={() => setEnding({ id: commitment.id, endsOn: '' })}
                        >
                          End
                        </button>
                        <button type="button" onClick={() => setRemoving(commitment.id)}>
                          Remove
                        </button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'active' && commitments !== null && commitments.length > 0 && (
        <p className="muted">
          <strong>End</strong> records that a commitment stopped, keeping its amount and cadence so
          a missed payment in a past month is still judged against the terms that applied then.{' '}
          <strong>Remove</strong> deletes one entered wrongly — the correction path, since a
          commitment cannot be edited. It asks first, because it cannot be undone.
        </p>
      )}

      {toast !== null && (
        <Toast message={toast} variant="ok" onDismiss={() => setToast(null)} />
      )}
    </section>
  );
}

/** "st", "nd", "rd", "th" — for "on about the 18th". */
function ordinal(day: number): string {
  if (day % 100 >= 11 && day % 100 <= 13) return 'th';
  if (day % 10 === 1) return 'st';
  if (day % 10 === 2) return 'nd';
  if (day % 10 === 3) return 'rd';
  return 'th';
}
